export type CityType = 'balneario' | 'interior' | 'pueblo-blanco';

export const CITY_TYPES: Record<string, CityType> = {
  'malaga': 'balneario',
  'marbella': 'balneario',
  'fuengirola': 'balneario',
  'torremolinos': 'balneario',
  'benalmadena': 'balneario',
  'mijas': 'balneario',
  'estepona': 'balneario',
  'nerja': 'balneario',
  'rincon-de-la-victoria': 'balneario',
  'velez-malaga': 'balneario',

  'antequera': 'interior',
  'ronda': 'interior',
  'cartama': 'interior',
  'coin': 'interior',
  'alhaurin-de-la-torre': 'interior',
  'alhaurin-el-grande': 'interior',
  'alora': 'interior',

  'benahavis': 'pueblo-blanco',
};

export function getCityType(slug: string): CityType {
  return CITY_TYPES[slug] || 'interior';
}

// ============================================================
// FAQ PERSIANAS
// ============================================================

interface FAQItem {
  q: string;
  a: string;
}

function faqPersianasBalneario(name: string): FAQItem[] {
  return [
    {
      q: `¿Cuánto cuesta reparar una persiana en ${name}?`,
      a: `En ${name}, la reparación suele oscilar entre 60 € y 380 €. El salitre marino de la costa acelera la corrosión de mecanismos y motores, por eso cambiar una cinta cuesta unos 60 €, un motor nuevo desde 150 €, y la sustitución de lamas desde 15 € por unidad. Si tu vivienda es una segunda residencia, también hacemos videodiagnóstico previo para ahorrarte visitas innecesarias.`,
    },
    {
      q: `¿En cuánto tiempo venís a ${name}?`,
      a: `Llegamos a ${name} en menos de 24 horas. En temporada alta (julio-agosto) la demanda en zonas costeras se dispara, por eso recomendamos contactar con antelación para urgentes. Para reparaciones estándar mantenemos el compromiso de 24h en todos los barrios, incluidas urbanizaciones y zonas de chalets.`,
    },
    {
      q: `¿Hacéis diagnóstico por videollamada antes de desplazarme?`,
      a: `Sí, especialmente recomendable si tienes una segunda residencia en ${name} y no estás allí físicamente. Envíanos un video corto por WhatsApp del problema y te damos un primer diagnóstico y presupuesto orientativo sin compromiso. Si la reparación es sencilla, te indicamos los pasos y ahorras la visita técnica.`,
    },
    {
      q: `¿Qué garantía tenéis en reparaciones costeras?`,
      a: `Garantía de 2 años por escrito en todas las reparaciones e instalaciones, tanto en piezas como en mano de obra. Utilizamos componentes específicos para ambientes marinos (acero inoxidable y aluminio anodizado) que resisten mejor el salitre de ${name}. Somos empresa legalmente establecida con seguro de responsabilidad civil.`,
    },
    {
      q: `¿Trabajáis fines de semana en ${name}?`,
      a: `Sí, los sábados por la mañana atendemos reparaciones programadas. Para domingos y festivos cubrimos solo urgencias (persiana bloqueada, caída de lamas, rotura de cristal por persiana desencajada). Puedes contactarnos por WhatsApp incluso fuera de horario y te responderemos en cuanto abramos.`,
    },
  ];
}

function faqPersianasInterior(name: string): FAQItem[] {
  return [
    {
      q: `¿Cuánto cuesta reparar una persiana en ${name}?`,
      a: `En ${name}, la reparación oscila entre 60 € y 400 €. El calor seco del interior y el polvo de zonas agrícolas pueden resecar cintas y atascar rodamientos más rápido que en la costa. Cambiar una cinta cuesta unos 60 €, reparar un motor desde 80 €, sustituirlo desde 150 €, y las lamas desde 15 €. El presupuesto exacto lo damos tras diagnóstico gratuito en tu domicilio.`,
    },
    {
      q: `¿En cuánto tiempo llegáis a ${name} desde la costa?`,
      a: `Cubrimos ${name} con técnico propio en menos de 24 horas. Aunque estamos ubicados en Málaga capital, hacemos rutas diarias hacia el interior (Antequera, Coín, Alhaurín, Ronda, Álora). Si nos contactas por la mañana, solemos poder pasar por la tarde o al día siguiente como máximo. El desplazamiento está incluido en el presupuesto.`,
    },
    {
      q: `¿Hacéis videodiagnóstico WhatsApp para ahorrar el desplazamiento?`,
      a: `Absolutamente. Dado que ${name} está algo alejado de la costa, el videodiagnóstico es una herramienta muy útil: envíanos un video corto por WhatsApp y te decimos si la avería requiere visita o si puedes solucionarlo tú mismo con nuestra guía. Sin compromiso, sin coste.`,
    },
    {
      q: `¿Qué garantía ofrecéis en zonas interiores?`,
      a: `Garantía de 2 años por escrito en piezas y mano de obra. En ${name}, donde las persianas soportan más horas de sol directo intenso en verano, utilizamos materiales con mayor resistencia UV (cintas reforzadas, motores con protección térmica Somfy). Certificado por escrito en cada intervención.`,
    },
    {
      q: `¿El desplazamiento a ${name} tiene coste adicional?`,
      a: `No. El desplazamiento a ${name} está incluido en el presupuesto. Aplicamos un pequeño suplemento en los precios de ciertos trabajos mayores (sustitución de motor completo, motorización de persiana manual) para cubrir el coste real del trayecto, pero la visita técnica y el diagnóstico son siempre gratuitos.`,
    },
  ];
}

function faqPersianasPuebloBlanco(name: string): FAQItem[] {
  return [
    {
      q: `¿Cuánto cuesta reparar una persiana en ${name}?`,
      a: `En ${name}, la reparación oscila entre 60 € y 420 €. Las casas de pueblo blanco suelen tener ventanas antiguas con huecos no estandarizados, lo que a veces obliga a adaptar mecanismos o realizar ajustes a medida. Cambiar una cinta cuesta unos 65 €, un motor nuevo desde 165 €, y la sustitución de lamas desde 15 €. Te damos presupuesto exacto tras visita gratuita.`,
    },
    {
      q: `¿Podéis acceder con furgoneta a las calles estrechas de ${name}?`,
      a: `Sí, conocemos bien el trazado de ${name}. Nuestras furgonetas son compactas y acceden a la mayoría de calles. En los callejones más estrechos del casco antiguo aparcamos lo más cerca posible y subimos el material a pie. Llevamos desde 2015 trabajando en pueblos blancos de la provincia: sabemos cómo organizar la logística sin complicarte.`,
    },
    {
      q: `¿El videodiagnóstico sirve para casas de pueblo blanco?`,
      a: `Sí, y es especialmente útil. Antes de desplazarnos a ${name}, nos envías un video por WhatsApp mostrando tanto la persiana averiada como el acceso (escaleras, calle, portal). Así evaluamos si necesitamos material específico o herramientas adicionales, y te damos presupuesto certero a la primera visita.`,
    },
    {
      q: `¿Qué garantía tenéis en pueblos blancos como ${name}?`,
      a: `Garantía de 2 años por escrito en piezas y mano de obra. En ${name}, donde muchas viviendas tienen persianas de aluminio de varias décadas, realizamos un diagnóstico completo del estado del cajón y guías antes de reparar. Si detectamos que la estructura está demasiado deteriorada, te avisamos para que decidas si reparar o sustituir.`,
    },
    {
      q: `¿Trabajáis en casas antiguas con persianas manuales de cuerda?`,
      a: `Claro que sí. En ${name} encontramos a menudo persianas manuales antiguas operadas con cuerda o cinta de tela. Las motorizamos desde 200 € instalando un motor tubular Somfy o Becker dentro del eje existente, respetando la estética original de la fachada. También reparamos los mecanismos manuales originales si prefieres mantenerlos.`,
    },
  ];
}

export function getCityFAQsPersianas(slug: string, name: string): FAQItem[] {
  const type = getCityType(slug);
  if (type === 'balneario') return faqPersianasBalneario(name);
  if (type === 'interior') return faqPersianasInterior(name);
  return faqPersianasPuebloBlanco(name);
}

// ============================================================
// FAQ TOLDOS
// ============================================================

function faqToldosBalneario(name: string): FAQItem[] {
  return [
    {
      q: `¿Cuánto cuesta un toldo nuevo en ${name}?`,
      a: `En ${name}, el precio de un toldo nuevo oscila entre 350 € y 5.000 € según el tipo y tamaño. Un toldo para balcón o terraza manual desde 350 €, un extensible motorizado de 4 metros desde 1.200 €, un cofre completo desde 1.500 €, y una pérgola bioclimática puede superar los 4.000 €. Las lonas que instalamos en zonas costeras llevan tratamiento anti-UV y anti-salitre específico para resistir el clima de ${name}.`,
    },
    {
      q: `¿Cuánto tarda la instalación en ${name}?`,
      a: `Desde que apruebas el presupuesto, la fabricación e instalación de un toldo estándar en ${name} suele tardar entre 15 y 20 días. En temporada alta (junio-agosto) los plazos pueden extenderse una semana más por la alta demanda en zonas costeras. Para toldos de balcón más sencillos, puede ser menos. Te confirmamos fecha exacta al contratar.`,
    },
    {
      q: `¿Hacéis videodiagnóstico WhatsApp para toldos?`,
      a: `Sí. Envíanos un video de tu terraza o balcón en ${name} por WhatsApp y te asesoramos gratis sobre el tipo de toldo más adecuado (extensible, cofre, pérgola), orientación respecto al sol, rango de precio orientativo, y si necesitas permisos. También evaluamos el estado de la barandilla o pared de anclaje. Sin compromiso.`,
    },
    {
      q: `¿Qué garantía tienen los toldos en zonas costeras?`,
      a: `Ofrecemos garantía de 5 años en el motor Somfy, 3 años en la lona (resistente a UV y salitre), y 2 años en mano de obra. En ${name}, donde el viento de levante puede ser intenso, recomendamos toldos cofre o pérgolas con sensores de viento. Todos incluyen certificado de garantía por escrito.`,
    },
    {
      q: `¿Necesito permisos en ${name}?`,
      a: `Depende de tu situación. Si vives en comunidad de vecinos en ${name}, es recomendable informar a la administración. Si el toldo sobresale de la fachada principal o supera ciertas dimensiones, puede requerir licencia del ayuntamiento. Nosotros te asesoramos sobre la normativa local de ${name} y gestionamos la documentación si es necesario.`,
    },
  ];
}

function faqToldosInterior(name: string): FAQItem[] {
  return [
    {
      q: `¿Cuánto cuesta un toldo en ${name}?`,
      a: `En ${name}, el precio de un toldo nuevo oscila entre 400 € y 5.200 €. Los precios son ligeramente superiores a la costa por el coste de transporte de materiales voluminosos al interior. Un toldo balcón manual desde 400 €, extensible motorizado desde 1.250 €, cofre desde 1.550 €, y pérgola desde 4.200 €. Las lonas son de alta resistencia UV, ideales para el sol intenso del interior.`,
    },
    {
      q: `¿Cuánto tarda la instalación en ${name}?`,
      a: `La fabricación e instalación de un toldo estándar en ${name} tarda entre 18 y 22 días desde la aprobación del presupuesto. Al estar alejado de la costa, organizamos las rutas de instalación para optimizar desplazamientos: normalmente pasamos una vez por semana con el equipo completo. Para pedidos urgentes, podemos adelantar en 3-4 días con suplemento.`,
    },
    {
      q: `¿Hacéis videodiagnóstico para toldos antes de desplazaros?`,
      a: `Sí, y es muy recomendable para ${name}. Envíanos fotos o video de la terraza, balcón o porche por WhatsApp y te orientamos sobre el modelo más adecuado, medidas aproximadas, y si la pared de anclaje es apta. Así la visita de medición posterior es más rápida y el presupuesto más ajustado. Sin coste ni compromiso.`,
    },
    {
      q: `¿Qué garantía tienen los toldos en zonas interiores?`,
      a: `Garantía de 5 años en motor, 3 años en lona, 2 años en mano de obra. En ${name}, donde las temperaturas veraniegas superan los 40 °C, utilizamos lonas de alta resistencia térmica que no se degradan con el calor seco. Todos nuestros toldos incluyen certificado de garantía por escrito y seguro de responsabilidad civil.`,
    },
    {
      q: `¿Necesito permisos en ${name}?`,
      a: `Depende del ayuntamiento de ${name} y de si vives en casa unifamiliar o comunidad. En zonas de casco antiguo o edificios protegidos, los toldos motorizados pueden requerir autorización. Te asesoramos sobre la normativa específica de ${name} antes de empezar, y si es necesario, gestionamos la documentación administrativa.`,
    },
  ];
}

function faqToldosPuebloBlanco(name: string): FAQItem[] {
  return [
    {
      q: `¿Cuánto cuesta un toldo en ${name}?`,
      a: `En ${name}, los toldos cuestan entre 450 € y 5.500 €. El acceso con furgoneta de material y las fachadas irregulares de los pueblos blancos pueden requerir adaptaciones de anclaje. Un toldo balcón desde 450 €, extensible motorizado desde 1.300 €, cofre desde 1.600 €. Evaluamos siempre el acceso y la pared de anclaje antes de presupuestar para evitar sorpresas.`,
    },
    {
      q: `¿Podéis instalar toldos en terrazas de pueblo blanco con paredes irregulares?`,
      a: `Sí, es nuestro día a día en pueblos blancos como ${name}. Las fachadas de cal o piedra irregular requieren tacos específicos y a veces soportería adicional. Nuestro técnico evalúa la pared de anclaje en la visita y, si es necesario, diseña una solución de fijación segura sin dañar la estética tradicional de la vivienda.`,
    },
    {
      q: `¿Hacéis videodiagnóstico para evaluar el anclaje?`,
      a: `Sí, especialmente útil en ${name}. Envíanos un video mostrando la terraza, la pared donde iría el toldo, y el acceso desde la calle. Así evaluamos si necesitamos material de anclaje especial, si cabe la furgoneta, o si debemos descargar a pie. Te damos presupuesto realista a la primera.`,
    },
    {
      q: `¿Qué garantía tienen los toldos en pueblos blancos?`,
      a: `Garantía de 5 años en motor, 3 años en lona, 2 años en mano de obra. En ${name}, donde las fachadas de cal pueden tener menor adherencia, utilizamos sistemas de fijación reforzados probados en cientos de instalaciones similares. Certificado por escrito en cada trabajo.`,
    },
    {
      q: `¿Necesito permisos en ${name}?`,
      a: `En muchos pueblos blancos como ${name}, el casco antiguo está protegido y cualquier modificación exterior requiere autorización municipal. Te asesoramos sobre si tu vivienda está en zona protegida y qué tipo de toldo (cofre, extensible, pérgola) es compatible. Si hace falta permiso, te guiamos en la tramitación.`,
    },
  ];
}

export function getCityFAQsToldos(slug: string, name: string): FAQItem[] {
  const type = getCityType(slug);
  if (type === 'balneario') return faqToldosBalneario(name);
  if (type === 'interior') return faqToldosInterior(name);
  return faqToldosPuebloBlanco(name);
}

// ============================================================
// FAQ MOSQUITERAS
// ============================================================

function faqMosquiterasBalneario(name: string): FAQItem[] {
  return [
    {
      q: `¿Cuánto cuesta una mosquitera en ${name}?`,
      a: `En ${name}, el precio de una mosquitera nueva oscila entre 60 € y 250 €. Una fija de aluminio para ventana pequeña desde 60 €, una enrollable desde 150 €, una corredera para puerta desde 120 €, y una plisada desde 180 €. En zonas costeras como ${name}, donde la humedad nocturna es alta, recomendamos mosquiteras de aluminio anodizado que no se oxidan y telas de fibra de vidrio resistentes al salitre.`,
    },
    {
      q: `¿En cuánto tiempo instaláis en ${name}?`,
      a: `Las mosquiteras fijas estándar se fabrican e instalan en ${name} en menos de una semana. Las enrollables y correderas personalizadas suelen tardar entre 5 y 15 días desde la medición. Reparaciones de cambio de tela las hacemos en una sola visita. En verano, reserva con antelación porque la demanda en la costa se multiplica.`,
    },
    {
      q: `¿Hacéis videodiagnóstico WhatsApp para mosquiteras?`,
      a: `Sí. Envíanos una foto o video de tu ventana o puerta en ${name} por WhatsApp y te asesoramos gratis sobre el modelo más adecuado (fija, enrollable, corredera, plisada), medidas orientativas, y precio aproximado. Es especialmente útil para segundas residencias: así vienes con el presupuesto decidido.`,
    },
    {
      q: `¿Qué garantía tenéis en mosquiteras costeras?`,
      a: `Garantía de 2 años en todos nuestros trabajos de instalación de mosquiteras en ${name}, tanto en el marco de aluminio anodizado como en la tela de fibra de vidrio. En zonas costeras aplicamos un tratamiento antioxidante adicional en los herrajes para prevenir la corrosión por salitre. Certificado por escrito.`,
    },
    {
      q: `¿Reparáis mosquiteras existentes en ${name}?`,
      a: `Sí, reparamos mosquiteras existentes en ${name}. El cambio de tela rota o agujereada cuesta desde 35 €. Si la estructura de aluminio está en buen estado, es la solución más económica. También cambiamos poleas, ruedas, cepillos y mecanismos de enrollables y correderas. Si el marco está oxidado por el salitre, te avisamos para valorar sustitución.`,
    },
  ];
}

function faqMosquiterasInterior(name: string): FAQItem[] {
  return [
    {
      q: `¿Cuánto cuesta una mosquitera en ${name}?`,
      a: `En ${name}, el precio oscila entre 65 € y 260 €. Una fija desde 65 €, enrollable desde 160 €, corredera desde 125 €, plisada desde 190 €. Los precios incluyen desplazamiento. En zonas interiores como ${name}, donde los mosquitos son más activos en primavera y otoño por los regadíos cercanos, las mosquiteras correderas y enrollables son las más demandadas por su durabilidad.`,
    },
    {
      q: `¿En cuánto tiempo instaláis en ${name}?`,
      a: `Las mosquiteras fijas se instalan en ${name} en 5-10 días. Las enrollables y correderas personalizadas tardan entre 7 y 18 días. Reparamos telas en una sola visita. Organizamos rutas semanales hacia el interior, por lo que si contactas el lunes o martes, solemos poder medir esa misma semana e instalar la siguiente.`,
    },
    {
      q: `¿Hacéis videodiagnóstico para mosquiteras?`,
      a: `Sí. Envíanos fotos de la ventana o puerta por WhatsApp y te decimos el modelo ideal y precio orientativo. Es muy útil para ${name} porque nos permite preparar el material exacto antes de desplazarnos, ahorrándote tiempo y evitando una segunda visita. Sin coste ni compromiso.`,
    },
    {
      q: `¿Qué garantía tenéis en ${name}?`,
      a: `Garantía de 2 años en marco de aluminio y tela de fibra de vidrio. En ${name}, donde el polvo y el calor seco pueden desgastar los mecanismos de enrollables, utilizamos poleas y resortes de mayor calibre. Si algo falla, volvemos a reparar sin coste dentro del período de garantía.`,
    },
    {
      q: `¿Reparáis mosquiteras en chalets y cortijos del interior?`,
      a: `Claro. En ${name} instalamos mosquiteras en viviendas unifamiliares, chalets y cortijos. Para puertas grandes de acceso a jardín o patio, recomendamos mosquiteras correderas de doble hoja. También hacemos reparaciones de telas rotas por mascotas o desgaste en puertas de terraza.`,
    },
  ];
}

function faqMosquiterasPuebloBlanco(name: string): FAQItem[] {
  return [
    {
      q: `¿Cuánto cuesta una mosquitera en ${name}?`,
      a: `En ${name}, el precio oscila entre 70 € y 270 €. Las ventanas de casas antiguas suelen tener huecos irregulares o contraventanas de madera gruesa, lo que requiere adaptaciones. Fija desde 70 €, enrollable desde 170 €, corredera desde 135 €, plisada desde 200 €. Medimos in situ para garantizar el ajuste perfecto en cada hueco particular.`,
    },
    {
      q: `¿Podéis instalar mosquiteras en ventanas antiguas de ${name}?`,
      a: `Sí, es una de nuestras especialidades. En ${name}, muchas casas del casco antiguo tienen ventanas con contraventanas de madera o marcos de hierro antiguos. Instalamos mosquiteras fijas por dentro o enrollables adaptadas al espacio disponible, sin dañar la carpintería original. Medimos y diseñamos la solución a medida.`,
    },
    {
      q: `¿Hacéis videodiagnóstico para ventanas de pueblo blanco?`,
      a: `Sí, muy recomendable para ${name}. Envíanos un video de la ventana mostrando el marco, la contraventana si la hay, y el espacio libre. Así decidimos si cabe una enrollable, una fija con marco reducido, o una plisada. Te damos presupuesto exacto sin necesidad de segunda visita.`,
    },
    {
      q: `¿Qué garantía tenéis en instalaciones de pueblo blanco?`,
      a: `Garantía de 2 años en marco y tela. En ${name}, donde las paredes de cal pueden complicar la fijación, utilizamos tacos y tornillería específica para este tipo de superficie. Si la fijación necesita reforzarse en el futuro, lo hacemos sin coste durante el primer año.`,
    },
    {
      q: `¿Reparáis mosquiteras en casas de pueblo blanco?`,
      a: `Sí, reparamos mosquiteras existentes en ${name}. Muchas casas antiguas ya tienen mosquiteras antiguas con tela desgastada o marco oxidado. Cambiamos solo la tela desde 40 €, o sustituimos el marco completo si está demasiado deteriorado. También adaptamos mosquiteras nuevas a huecos no estándar.`,
    },
  ];
}

export function getCityFAQsMosquiteras(slug: string, name: string): FAQItem[] {
  const type = getCityType(slug);
  if (type === 'balneario') return faqMosquiterasBalneario(name);
  if (type === 'interior') return faqMosquiterasInterior(name);
  return faqMosquiterasPuebloBlanco(name);
}